// Notification Sound Service for Web Push Notifications
// Provides Web Audio API fallback for reliable notification sounds

class NotificationSoundService {
    constructor() {
        this.audioContext = null;
        this.soundEnabled = false;
        this.initialized = false;
    }

    // Initialize the audio context after user gesture
    async initialize() {
        if (this.initialized) return true;

        try {
            // Create AudioContext after user gesture
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            
            // Resume context if suspended
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }
            
            this.soundEnabled = true;
            this.initialized = true;
            
            // Store in localStorage for persistence
            localStorage.setItem('notificationSoundEnabled', 'true');
            // Clear pending enable flag
            localStorage.removeItem('notificationSoundPendingEnable');
            
            console.log('[NotificationSound] ✅ Sound system initialized');
            return true;
            
        } catch (error) {
            console.error('[NotificationSound] ❌ Failed to initialize:', error);
            return false;
        }
    }

    // Generate and play a notification beep
    playNotificationBeep() {
        if (!this.audioContext || !this.soundEnabled) {
            console.log('[NotificationSound] Sound not enabled or context not available');
            return;
        }

        try {
            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            
            // Create a pleasant notification tone (800Hz)
            oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
            gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
            
            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + 0.3);
            
            console.log('[NotificationSound] 🔊 Played notification beep');
            
        } catch (error) {
            console.error('[NotificationSound] ❌ Error playing beep:', error);
        }
    }

    // Play a test sound
    playTestSound() {
        if (!this.soundEnabled) {
            console.log('[NotificationSound] Sound not enabled. Call initialize() first.');
            return false;
        }
        
        this.playNotificationBeep();
        return true;
    }

    // Check if sound is enabled
    isSoundEnabled() {
        return this.soundEnabled && this.initialized;
    }

    // Enable sound (requires user gesture)
    async enableSound() {
        return await this.initialize();
    }

    // Disable sound
    disableSound() {
        this.soundEnabled = false;
        localStorage.setItem('notificationSoundEnabled', 'false');
        console.log('[NotificationSound] 🔇 Sound disabled');
    }

    // Auto-initialize if previously enabled
    async autoInitialize() {
        const wasEnabled = localStorage.getItem('notificationSoundEnabled') === 'true';
        if (wasEnabled && !this.initialized) {
            // Don't try to initialize AudioContext without user gesture
            // Just mark that we should enable it when user interacts
            console.log('[NotificationSound] Auto-initialization deferred until user gesture');
            // Set a flag that we should auto-enable on first interaction
            localStorage.setItem('notificationSoundPendingEnable', 'true');
        }
    }
}

// Create singleton instance
const notificationSoundService = new NotificationSoundService();

export default notificationSoundService;